"use client";

import Button from "@/components/common/Button";
import FullView from "@/components/common/FullView";
import { PATH } from "@/constants/path";
import { useAnalytics } from "@/hooks/useAnalytics";
import { FunnelEvent, UserEvent } from "@/types/analytics";
import { cn } from "@/utils/cn";

// 섹션 컨테이너
const sectionClasses = cn(
  "relative flex h-full w-full max-w-[1200px] flex-col items-center", // 기본 레이아웃
  "desktop:flex-row desktop:items-center desktop:justify-between", // 데스크탑 방향/정렬
  "desktop:gap-12", // 간격
);

// 왼쪽 텍스트 + 버튼 컨테이너
const leftContainerClasses = cn(
  "z-10 flex flex-col gap-8", // 레이아웃/간격
  "items-center px-side", // 정렬/모바일 패딩
  "desktop:max-w-[550px] desktop:flex-shrink-0", // 데스크탑 크기 (고정 너비)
  "desktop:items-start desktop:px-0", // 데스크탑 정렬/패딩
);

// 메인 타이틀
const titleClasses = cn(
  "text-center text-36 font-bold", // 기본 타이포그래피
  "leading-[1.5] tracking-tight", // 줄간격/자간
  "desktop:text-left desktop:text-44", // 데스크탑 정렬
);

// 서브 텍스트
const subtextClasses = cn(
  "text-14 text-center", // 모바일: 14px, 가운데 정렬
  "leading-[1.45] tracking-tight text-black/55", // 줄간격/자간/색상
  "tablet:text-18", // 태블릿: 18px (768px+)
  "desktop:text-24 desktop:text-left", // 데스크탑: 20px, 왼쪽 정렬 (1024px+)
);

// 버튼 영역
const buttonGroupClasses = cn(
  "flex flex-wrap gap-4", // 레이아웃/간격 (줄바꿈 허용)
  "justify-center", // 모바일: 가운데 정렬
  "desktop:justify-start", // 데스크탑: 왼쪽 정렬
);

// 커스텀 버튼 스타일
const blackButtonClasses = cn(
  "rounded-xl font-medium leading-[1.45]", // 기본 스타일
  "bg-black text-white", // 색상
  "text-14 px-4 py-2.5", // 모바일: 14px, 패딩 (높이 줄임)
  "tablet:text-18", // 태블릿: 18px (768px+)
  "desktop:text-18", // 데스크탑: 18px (1024px+)
  "hidden header:inline-flex", // 1060px 이상에서만 표시
);

const whiteButtonClasses = cn(
  "rounded-xl font-medium leading-[1.45]", // 기본 스타일
  "bg-white text-black border-2 border-black/15", // 색상/테두리
  "text-14 px-4 py-2.5", // 모바일: 14px, 패딩 (높이 줄임)
  "tablet:text-18", // 태블릿: 18px (768px+)
  "desktop:text-18", // 데스크탑: 18px (1024px+)
);

// 오른쪽 이미지 외부 컨테이너
const imageOuterClasses = cn(
  "relative w-full", // 기본 레이아웃
  "mt-12", // 모바일 마진
  "desktop:mt-0 desktop:flex-1 desktop:max-w-[900px]", // 데스크탑 마진/크기/최대 너비
  "desktop:flex desktop:justify-end", // 데스크탑 오른쪽 정렬
);

function HeroSection() {
  const { trackFunnelEvent, trackUserEvent } = useAnalytics();

  const handleServiceExperience = () => {
    trackFunnelEvent(FunnelEvent.FEATURE_EXPLORED, {
      feature: "service_demo",
      location: "hero_section",
    });
  };

  const handleDownloadIntro = () => {
    trackFunnelEvent(FunnelEvent.CONTENT_DOWNLOADED, {
      content_type: "service_introduction",
      location: "hero_section",
    });
  };

  const handleContactClick = () => {
    trackUserEvent(UserEvent.PAGE_NAVIGATED, {
      destination: "request_page",
      source: "hero_section_contact_button",
    });
  };

  return (
    <FullView as="section" className="w-full">
      <div className={sectionClasses}>
        {/* 왼쪽 텍스트 + 버튼 영역 */}
        <div className={leftContainerClasses}>
          {/* 메인 타이틀 */}
          <div className={titleClasses}>
            <h2 className="mb-0">채용 공고로</h2>
            <h2 className="mb-0">만날 수 없는 핵심 인재</h2>
            <h2>1주 안에 커피챗까지</h2>
          </div>

          {/* 서브 텍스트 */}
          <div className={subtextClasses}>
            <p className="mb-0">희소 포지션·시니어·리드급 채용에 어려움을 겪는 팀을 위해</p>
            <p>AI 리크루터로 숨은 인재를 발굴하여 연결합니다.</p>
          </div>

          {/* 버튼 영역 */}
          <div className={buttonGroupClasses}>
            <Button href={PATH.DEMO_CLIENT} className={blackButtonClasses} onClick={handleServiceExperience}>
              서비스 체험하기
            </Button>
            <div className="flex w-full flex-col gap-4 header:w-auto header:flex-row">
              <Button
                href={PATH.COMPANY_INTRO}
                className={cn(whiteButtonClasses, "flex w-full items-center justify-center header:w-auto")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleDownloadIntro}
              >
                서비스 소개서 다운받기
              </Button>
              <Button
                href="/request"
                className={cn(blackButtonClasses, "flex w-full items-center justify-center header:hidden")}
                onClick={handleContactClick}
              >
                문의하기
              </Button>
            </div>
          </div>
        </div>

        {/* 오른쪽 이미지 영역 */}
        <div className={imageOuterClasses}>
          <picture>
            <source media="(min-width: 1024px)" srcSet="/image/home/hero-desktop.png" />
            <source media="(min-width: 768px)" srcSet="/image/home/hero-tablet.png" />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/image/home/hero-mobile.png"
              alt="AI 리크루터 플랫폼"
              fetchPriority="high"
              className="w-full object-contain aspect-[1428/1588] tablet:aspect-[3129/2009] desktop:aspect-[2704/2416]"
            />
          </picture>
        </div>
      </div>
    </FullView>
  );
}

export default HeroSection;
